import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from '@tanstack/react-table';
import { FaEye, FaPen, FaTrash, FaCheck, FaTimes, FaSyncAlt } from 'react-icons/fa';
import { SiLangchain } from "react-icons/si";
import useReportStore from '../../store/report-store';
import useUserStore from '../../store/user-store'
import useOrganisationStore from '../../store/organisations-store';
import useToolStore from '../../store/tools-store.js';
import apiClient from '../../store/api/apiClient';
import Loader from '../../shared/components/layout/Loader';
import DataTable from '../../shared/components/table/DataTable';
import ListFooter from '../../shared/components/table/ListFooter';
import DeleteModal from '../../shared/components/modal/DeleteModal';
import useDebounce from '../../shared/hooks/useDebounce';
import useSearchableFilter from '../../shared/hooks/useSearchableFilter';
import { formatDate, todayYmd } from '../../shared/lib/formatters';
import { renderLargeCell } from '../../shared/lib/renderLargeCell';
import { handleUpdateStatus } from '../../shared/lib/adminApiHelpers';
import { env } from '../../shared/config/env';
import ReportsFilters from './ReportsFilters';
import ViewReportModal from './ViewReportModal';
import EditReportModal from './EditReportModal';

const columnHelper = createColumnHelper();

const getReportsHeaderClassName = (header) => {
  const id = header.column.id;
  if (id === 'actions') return '';
  if (id.length < 10) return 'min-w-36 sm:min-w-44';
  if (id.length < 15) return 'min-w-52 sm:min-w-52';
  return 'min-w-64 sm:min-w-64';
};

const isContentDeleted = (report) => report?.metadata?.content_deleted === true;

const getReportsRowClassName = (row) => {
  if (isContentDeleted(row.original)) return '!bg-[#f2f3f5]';
  if (row.original.status === 'completed' && !row.original.report_fetched) return 'bg-red-50';
  if (row.original.status === 'processing') return 'bg-orange-50';
  return '';
};

const ReportsPage = () => {
  const { reports, totalReports, totalPages, fetchReports, fetchReport, setReport, deleteReport, editReport, retryLoading, setRetryLoading, setActiveFilters} = useReportStore();
  const { searchUsers } = useUserStore();
  const { searchOrganisations } = useOrganisationStore();
  const { tools, fetchTools } = useToolStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [timestampsExportLoading, setTimestampsExportLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedTool, setSelectedTool] = useState('');
  const [filterFailed, setFilterFailed] = useState(null);
  const [filterIntegrations, setFilterIntegrations] = useState(false);
  const [selectedIntegrationClient, setSelectedIntegrationClient] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [filtersMenuOpen, setFiltersMenuOpen] = useState(false);
  const filtersPanelRef = useRef(null);

  const {
    value: selectedOrg,
    label: selectedOrgLabel,
    onChange: setOrgSelection,
    clear: clearOrgFilter,
    loadOptions: loadOrgOptions,
  } = useSearchableFilter(searchOrganisations);

  const getUserSearchExtras = useCallback(() => ({ orgId: selectedOrg }), [selectedOrg]);
  const {
    value: selectedUser,
    label: selectedUserLabel,
    onChange: setUserSelection,
    clear: clearUserFilter,
    loadOptions: loadUserOptions,
  } = useSearchableFilter(searchUsers, getUserSearchExtras);

  const debouncedGlobalFilter = useDebounce(globalFilter, 500);
  const search = useMemo(() => `${debouncedGlobalFilter}`.trim(), [debouncedGlobalFilter]);

  const handleOrgFilterChange = useCallback(
    (id, option) => {
      setOrgSelection(id, option);
      clearUserFilter();
      setCurrentPage(1);
    },
    [setOrgSelection, clearUserFilter]
  );

  const handleUserFilterChange = useCallback(
    (id, option) => {
      setUserSelection(id, option);
      setCurrentPage(1);
    },
    [setUserSelection]
  );

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchTools(),
        fetchReports(
          currentPage,
          search,
          selectedUser,
          selectedOrg,
          selectedTool,
          filterFailed,
          filterIntegrations,
          selectedIntegrationClient,
          filterStartDate,
          filterEndDate
        ),
      ]);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  }, [
    fetchReports,
    currentPage,
    search,
    selectedUser,
    selectedOrg,
    fetchTools,
    selectedTool,
    filterFailed,
    filterIntegrations,
    selectedIntegrationClient,
    filterStartDate,
    filterEndDate,
  ]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Sync local filter state to the report store so WebSocket handler can respect them
  useEffect(() => {
    setActiveFilters({
      search,
      userId: selectedUser,
      orgId: selectedOrg,
      toolId: selectedTool,
      filterFailed,
      integrationOnly: filterIntegrations,
      integrationClient: selectedIntegrationClient,
      startDate: filterStartDate,
      endDate: filterEndDate,
    });
  }, [
    search,
    selectedUser,
    selectedOrg,
    selectedTool,
    filterFailed,
    filterIntegrations,
    selectedIntegrationClient,
    filterStartDate,
    filterEndDate,
    setActiveFilters,
  ]);

  useEffect(() => {
    return () => {
      setActiveFilters({
        search: '',
        userId: '',
        orgId: '',
        toolId: '',
        filterFailed: null,
        integrationOnly: false,
        integrationClient: '',
        startDate: '',
        endDate: '',
      });
    };
  }, [setActiveFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedGlobalFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterIntegrations, selectedIntegrationClient]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStartDate, filterEndDate]);

  useEffect(() => {
    if (!filtersMenuOpen) return;
    const close = (e) => {
      if (filtersPanelRef.current && !filtersPanelRef.current.contains(e.target)) {
        setFiltersMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [filtersMenuOpen]);

  const activeFilterCount = [
    selectedTool,
    selectedOrg,
    selectedUser,
    filterFailed,
    filterIntegrations,
    selectedIntegrationClient,
    filterStartDate,
    filterEndDate,
  ].filter(Boolean).length;

  const hasActivePanelFilters = activeFilterCount > 0;

  const clearPanelFilters = () => {
    clearUserFilter();
    clearOrgFilter();
    setSelectedTool('');
    setFilterFailed(null);
    setFilterIntegrations(false);
    setSelectedIntegrationClient('');
    setFilterStartDate('');
    setFilterEndDate('');
    setCurrentPage(1);
  };

  const handleTimestampsExport = async () => {
    if (!selectedOrg) return;
    setTimestampsExportLoading(true);
    try {
      const params = {
        orgId: selectedOrg,
        integrationOnly: filterIntegrations,
        ...(filterEndDate ? { endDate: filterEndDate } : {}),
        ...(filterStartDate ? { startDate: filterStartDate } : {}),
        ...(selectedUser ? { userId: selectedUser } : {}),
        ...(selectedTool ? { toolId: selectedTool } : {}),
        ...(filterIntegrations && selectedIntegrationClient ? { integrationClient: selectedIntegrationClient } : {}),
      };
      const response = await apiClient.getBlob('/admin/reports/export-timestamps', params);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_timestamps_${filterStartDate || 'all'}_${filterEndDate || todayYmd()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Timestamps CSV downloaded');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Timestamps export failed');
    } finally {
      setTimestampsExportLoading(false);
    }
  };

  const handleReportsExport = async () => {
    if (!filterStartDate) {
      toast.info('Please select a start date before exporting.');
      return;
    }
    setExportLoading(true);
    try {
      const params = {
        startDate: filterStartDate,
        integrationsOnly: filterIntegrations,
        ...(filterEndDate ? { endDate: filterEndDate } : {}),
        ...(selectedOrg ? { orgId: selectedOrg } : {}),
        ...(selectedUser ? { userId: selectedUser } : {}),
        ...(selectedTool ? { toolId: selectedTool } : {}),
        ...(filterIntegrations && selectedIntegrationClient ? { integrationClient: selectedIntegrationClient } : {}),
      };
      const response = await apiClient.getBlob('/admin/reports/export', params);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reports_${filterStartDate}_${filterEndDate || todayYmd()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleViewClick = useCallback((report) => {
    setReport(report);
    setSelectedReport(report);
    setIsViewModalOpen(true);
  }, [setReport]);

  const handleEditClick = useCallback((report) => {
    setReport(report);
    setSelectedReport(report);
    setIsEditModalOpen(true);
  }, [setReport]);

  const handleDeleteClick = (report) => {
    setSelectedReport(report);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteReport = async (id) => {
    try {
      await deleteReport(id);
      toast.success('Report deleted successfully');
      setIsDeleteModalOpen(false);
    }
    catch (error) {
      console.error('Error deleting report', error);
      toast.error('Report could not be deleted');
    }
  }

  const handleEditReport = async (id, updatedData) => {
    try {
      await editReport(id, updatedData);
      toast.success('Report edited successfully');
      setIsEditModalOpen(false);
    }
    catch (error) {
      console.error('Error editing report', error);
      toast.error('Report could not be edited');
    }
  }

  const handleRetry = useCallback(async (id) => {
    setRetryLoading(id, true);
    await handleUpdateStatus({ documentId: id, status: 'retry' });
    await fetchReport(id);
  }, [setRetryLoading, fetchReport]);

  const goToLangChainRunnableSequence = (run_id) => {
    const isEu = run_id.startsWith('eu:');
    const id = isEu ? run_id.slice(3) : run_id;
    const project = isEu
      ? env.langsmithEuProjectUrl
      : 'https://smith.langchain.com/o/96ad2d11-9c39-5d7c-bb83-eeeb6f9481e0/projects/p/ddfe9499-e92e-468e-b444-980da965bf26';
    if (!project) return;
    window.open(`${project}/r/${id}`, '_blank');
  }

  const columns = useMemo(() => {
    const toolFields = ['patient', 'doctor', 'nurse', 'location'].map(field => 
      columnHelper.accessor(`fields.${field}`, {
        header: field.charAt(0).toUpperCase() + field.slice(1),
        cell: info => info.getValue() || '-'
      })
    );

    const integrationColumn = columnHelper.accessor('partner', {
      header: 'Integration',
      cell: (info) => info.getValue() || '-',
    });

    return [
      columnHelper.accessor('row_number', {
        header: 'Report #',
      }),
      columnHelper.accessor('id', {
        header: 'ID',
      }),
      columnHelper.accessor('user_name', {
        header: 'User Name',
      }),
      columnHelper.accessor('tool_name', {
        header: 'Tool Name',
      }),
      ...(filterIntegrations ? [integrationColumn] : []),
      ...toolFields,
      columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ row, getValue }) => (
          isContentDeleted(row.original) ? 'deleted' : (getValue() || '-')
        ),
      }),
      columnHelper.accessor('audio_language', {
        header: 'Audio Language',
      }),
      columnHelper.accessor('report_language', {
        header: 'Report Language',
      }),
      columnHelper.accessor('error', {
        header: 'Error Description',
        cell: (info) => renderLargeCell(expandedRows, setExpandedRows, info, 'error'),
      }),
      columnHelper.accessor('created_at', {
        header: 'Created At',
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('report_words', {
        header: 'Report Words',
      }),
      columnHelper.accessor('words_deducted', {
        header: 'Actual Words deducted',
      }),
      columnHelper.accessor('template_name', {
        header: 'Template',
      }),
      columnHelper.accessor('uses_custom_template', {
        header: 'Uses User Template',
        cell: info => info.getValue() === true ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-600" />,
      }),
      columnHelper.accessor('report_fetched', {
        header: 'Report Fetched',
        cell: info => info.getValue() === true ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-600" />,
      }),
      columnHelper.accessor('comment', {
        header: 'Comment',
        cell: (info) => renderLargeCell(expandedRows, setExpandedRows, info, 'comment'),
      }),
      columnHelper.accessor('retried_at', {
        header: 'Retried At',
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('meta_type', {
        header: 'Meta Type',
      }),
      columnHelper.accessor('user_id', {
        header: 'User ID',
      }),      
      columnHelper.accessor((row) => row.transcriptions?.gladia, {
        header: 'Gladia',
        cell: (info) => renderLargeCell(expandedRows, setExpandedRows, info, 'gladia')
      }),
      columnHelper.accessor((row) => row.transcriptions?.gemini, {
        header: 'Gemini',
        cell: (info) => renderLargeCell(expandedRows, setExpandedRows, info, 'gemini')
      }),
      columnHelper.accessor((row) => row.transcriptions?.speechmatics, {
        header: 'Speechmatics',
        cell: (info) => renderLargeCell(expandedRows, setExpandedRows, info, 'speechmatics')
      }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => {
          const isProcessing = row.original.status === 'processing';
          const isFailed = row.original.status === 'completed' && !row.original.report_fetched;
          const disableRetry = ["Failed to upload file", "Failed to fetch signed url"].includes(row.original.error);
          return (
            <div className="flex justify-around items-center space-x-4">
              <FaEye
                title="View"
                onClick={() => handleViewClick(row.original)}
                className="cursor-pointer text-green-500"
              />
              <FaPen
                title="Edit"
                onClick={() => handleEditClick(row.original)}
                className="cursor-pointer text-blue-500"
              />
              <FaTrash
                title="Delete"
                onClick={() => handleDeleteClick(row.original)}
                className="cursor-pointer text-red-600"
              />
              {(row.original.langchain_run_id && !isProcessing) && <SiLangchain
                title="Langchain"
                className="cursor-pointer"
                size={28}
                onClick={() => goToLangChainRunnableSequence(row.original.langchain_run_id)}
              />
              }
              {(isFailed || isProcessing) && (
                retryLoading[row.original.id] || isProcessing ? (
                  <div title='Processing'>
                    <Loader className="text-yellow-500" />
                  </div>
                ) : (
                  <FaSyncAlt
                    title={disableRetry ? 'File missing' : 'Retry'}
                    onClick={disableRetry ? null : () => handleRetry(row.original.id)}
                    className={`text-yellow-500 ${disableRetry ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  />
                )
              )}
            </div>
          )
        },
      }),
    ];
  }, [handleViewClick, handleEditClick, handleRetry, retryLoading, expandedRows, filterIntegrations]);
  
  const table = useReactTable({
    data: reports,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const noReportsFound = table.getRowModel().rows.length === 0;

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <h1 className="text-2xl font-semibold mb-4 py-1">Reports List</h1>
      <ReportsFilters
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        filtersMenuOpen={filtersMenuOpen}
        setFiltersMenuOpen={setFiltersMenuOpen}
        filtersPanelRef={filtersPanelRef}
        hasActivePanelFilters={hasActivePanelFilters}
        activeFilterCount={activeFilterCount}
        clearPanelFilters={clearPanelFilters}
        tools={tools}
        selectedTool={selectedTool}
        setSelectedTool={setSelectedTool}
        selectedOrg={selectedOrg}
        selectedOrgLabel={selectedOrgLabel}
        setSelectedOrg={handleOrgFilterChange}
        selectedUser={selectedUser}
        selectedUserLabel={selectedUserLabel}
        setSelectedUser={handleUserFilterChange}
        loadOrgOptions={loadOrgOptions}
        loadUserOptions={loadUserOptions}
        filterFailed={filterFailed}
        setFilterFailed={setFilterFailed}
        filterIntegrations={filterIntegrations}
        setFilterIntegrations={setFilterIntegrations}
        selectedIntegrationClient={selectedIntegrationClient}
        setSelectedIntegrationClient={setSelectedIntegrationClient}
        filterStartDate={filterStartDate}
        setFilterStartDate={setFilterStartDate}
        filterEndDate={filterEndDate}
        setFilterEndDate={setFilterEndDate}
        handleTimestampsExport={handleTimestampsExport}
        timestampsExportLoading={timestampsExportLoading}
        handleReportsExport={handleReportsExport}
        exportLoading={exportLoading}
      />
      <DataTable
        table={table}
        columnsCount={columns.length}
        isLoading={loading}
        emptyMessage="No reports found"
        variant="reports"
        stickyActions
        getHeaderClassName={getReportsHeaderClassName}
        getRowClassName={getReportsRowClassName}
      />
      <ListFooter
        total={totalReports}
        totalPages={totalPages}
        page={currentPage}
        setPage={setCurrentPage}
        isLoading={loading}
        isEmpty={noReportsFound}
        itemLabel="reports"
      />

      <ViewReportModal
        isOpen={isViewModalOpen}
        onRequestClose={() => setIsViewModalOpen(false)}
        report={selectedReport}
      />

      <EditReportModal
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        report={selectedReport}
        onEdit={handleEditReport}
        fetchReport={fetchReport}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        data={{...selectedReport, dataName: 'report'}}
        onDelete={handleDeleteReport}
      />
    </div>
  );
};

export default ReportsPage;
